import React, { Component, createRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";

import "react-bootstrap-table/dist/react-bootstrap-table.min.css";
import CurrencyRoundCard from "./CurrencyRoundCard";
import CurrenciesCard from "./CurrenciesCard";
import bakongIcon from "@/assets/img/bakong-icon.png";
import indirectAccountIcon from "@/assets/img/indirect-account-icon.png";

import { APP_CONST, getStoredItem } from "@/store/dataStore";
import accounts from "@/api/accounts";
import reports from "@/api/reports";

import LoadingOverlay from "@/views/Components/LoadingOverlay";

import { dollarViewFormatter, rielViewFormatter } from "@/util/formatter";
import { handleError } from "@/util/errorHandler";
import styles from "./styles.module.scss";
import DnDTable from "@/views/Components/DnDTable";

class TotalAccount extends Component {
  state = {
    isLoading: true,
    mainBalanceKhr: 0,
    mainBalanceUsd: 0,
    indirectAccountsCount: 0,
    indirectBalanceKhr: 0,
    indirectBalanceUsd: 0,
    branchAccountsCount: 0,
    branchBalanceKhr: 0,
    branchBalanceUsd: 0,
    directBalanceKhr: 0,
    directBalanceUsd: 0,
    totalDomainBalanceKhr: 0,
    totalDomainBalanceUsd: 0,
    branchId: null,
    isDirectAccountDataLoading: false,
    directAccountData: [],
    directAccountTotalEntities: 0,
    isIndirectAccountDataLoading: false,
    indirectAccountData: [],
    indirectAccountTotalEntities: 0,
  };

  componentDidMount() {
    this.firstDataLoad();
    this.loadBranchId();
  }

  firstDataLoad = async () => {
    try {
      this.setState({ isLoading: true });

      /*
       * DO NOT RUN 'accounts.getAllAccountsSummary' IN PARALLEL
       * IT PRODUCES BUGS IN BACKEND
       */
      const [indirectAccount, domainBalance] = await Promise.all([
        accounts.getAllAccountsSummary({
          type: "ALL",
          branch: "CUSTOMER", // Indirect account
        }),
        accounts.getAllAccountsSummary({
          type: "ALL",
          branch: "DOMAIN",
        }),
        this.fetchMainAccountBalance(),
      ]);
      const [branchAccount, directAccount] = await Promise.all([
        accounts.getAllAccountsSummary({
          type: "ALL",
          branch: "BRANCH",
        }),
        accounts.getAllAccountsSummary({
          type: "ALL",
          branch: "DIRECT_ACCOUNT",
        }),
      ]);

      this.setState({
        indirectAccountsCount: indirectAccount.totalAccount,
        indirectBalanceKhr: indirectAccount.totalAmounts["khr#nbc"],
        indirectBalanceUsd: indirectAccount.totalAmounts["usd#nbc"],
        branchAccountsCount: branchAccount.totalAccount,
        branchBalanceKhr: branchAccount.totalAmounts["khr#nbc"],
        branchBalanceUsd: branchAccount.totalAmounts["usd#nbc"],
        directBalanceKhr: directAccount.totalAmounts["khr#nbc"],
        directBalanceUsd: directAccount.totalAmounts["usd#nbc"],
        totalDomainBalanceKhr: domainBalance.totalAmounts["khr#nbc"],
        totalDomainBalanceUsd: domainBalance.totalAmounts["usd#nbc"],
      });
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  fetchMainAccountBalance = async () => {
    try {
      const userInfo = JSON.parse(getStoredItem(APP_CONST.USER_INFO));
      const data = await accounts.getMainAccountBalance(
        userInfo.participantBankCode,
      );

      this.setState({
        mainBalanceUsd: data.assets["usd#nbc"],
        mainBalanceKhr: data.assets["khr#nbc"],
      });
    } catch (err) {
      handleError(err);
    }
  };

  loadBranchId = async () => {
    try {
      if (!this.state.branchId) {
        const branches = await accounts.getBranchesList();
        const { id: branchId } = branches.find(({ headBranch }) => headBranch);
        this.setState({ branchId });
      }
    } catch (err) {
      handleError(err);
    }
  };

  onLoadDirectAccountData = async params => {
    try {
      this.setState({ isDirectAccountDataLoading: true });
      const { data, total } = await accounts.getListUserUnderDomain({
        ...params,
        branch: "DIRECT_ACCOUNT",
      });
      this.setState({
        directAccountData: data,
        directAccountTotalEntities: total,
      });
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isDirectAccountDataLoading: false });
    }
  };

  onLoadIndirectAccountData = async params => {
    try {
      this.setState({ isIndirectAccountDataLoading: true });
      const { data, total } = await accounts.getListUserUnderDomain({
        ...params,
        branch: "INDIRECT_ACCOUNT",
      });
      this.setState({
        indirectAccountData: data,
        indirectAccountTotalEntities: total,
      });
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isIndirectAccountDataLoading: false });
    }
  };

  directRef = createRef();
  indirectRef = createRef();

  onIndirectToDirectDrop = async () => {
    try {
      this.setState({ isLoading: true });

      const { branchId } = this.state;
      const irohaAccounts =
        this.indirectRef.current && this.indirectRef.current.state.selectedRows;
      await accounts.putAccountsIntoBranch({ branchId, irohaAccounts });
      await this.updateBranchData();
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  onDirectToIndirectDrop = async () => {
    try {
      this.setState({ isLoading: true });

      const { branchId } = this.state;
      const irohaAccounts =
        this.directRef.current && this.directRef.current.state.selectedRows;
      await accounts.putAccountsOutOfBranch({ branchId, irohaAccounts });
      await this.updateBranchData();
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  updateBranchData = async () => {
    /*
     * DO NOT RUN 'accounts.getAllAccountsSummary' IN PARALLEL
     * IT PRODUCES BUGS IN BACKEND
     */
    const [indirectAccount] = await Promise.all([
      accounts.getAllAccountsSummary({
        type: "ALL",
        branch: "CUSTOMER", // Indirect account
      }),
      this.directRef.current.props.loadData(),
    ]);
    const [branchAccount, directAccount] = await Promise.all([
      accounts.getAllAccountsSummary({
        type: "ALL",
        branch: "BRANCH",
      }),
      accounts.getAllAccountsSummary({
        type: "ALL",
        branch: "DIRECT_ACCOUNT",
      }),
      this.indirectRef.current.props.loadData(),
    ]);

    this.setState({
      indirectAccountsCount: indirectAccount.totalAccount,
      indirectBalanceKhr: indirectAccount.totalAmounts["khr#nbc"],
      indirectBalanceUsd: indirectAccount.totalAmounts["usd#nbc"],
      branchAccountsCount: branchAccount.totalAccount,
      branchBalanceKhr: branchAccount.totalAmounts["khr#nbc"],
      branchBalanceUsd: branchAccount.totalAmounts["usd#nbc"],
      directBalanceKhr: directAccount.totalAmounts["khr#nbc"],
      directBalanceUsd: directAccount.totalAmounts["usd#nbc"],
    });
  };

  viewFormatter = (cell, row) => {
    return (
      <Link
        to={{
          pathname: "/account_detail/" + row.accountId,
          state: {
            data: row,
            previousLocation: {
              ...this.props.location,
            },
          },
        }}>
        <div>
          <i
            className="fa fa-search"
            style={{
              color: "#D15050",
            }}
            data-cy="view-account"
          />
        </div>
      </Link>
    );
  };

  customerColumns = [
    {
      title: "View",
      width: 50,
      dataFormat: this.viewFormatter,
      dataAlign: "center",
      style: { width: "5%", minWidth: "45px" },
    },
    {
      title: "Type",
      width: 50,
      dataField: "accountType",
      dataAlign: "center",
      dataFormat: accountType =>
        accountType === "MOBILE" ? (
          <i className="fa fa-mobile fa-lg" />
        ) : (
          <i className="fa fa-laptop fa-lg" />
        ),
      style: { width: "5%", minWidth: "45px" },
    },
    {
      title: "Name",
      dataSort: true,
      dataField: "fullName",
      dataFormat: (_, row) => row.organizationName || row.fullName,
      style: { width: "10%", minWidth: "120px" },
    },
    {
      title: "Account ID",
      dataSort: true,
      dataField: "accountId",
      style: { width: "10%", minWidth: "90px" },
    },
    {
      title: "KHR",
      width: 110,
      dataSort: true,
      dataField: "balanceKhr",
      dataFormat: rielViewFormatter,
    },
    {
      title: "USD",
      width: 110,
      dataSort: true,
      dataField: "balanceUsd",
      dataFormat: dollarViewFormatter,
    },
  ];

  branchColumns = [
    {
      title: "View",
      width: 50,
      dataFormat: this.viewFormatter,
      dataAlign: "center",
      style: { width: "5%", minWidth: "45px" },
    },
    {
      title: "Type",
      width: 50,
      dataField: "accountType",
      dataAlign: "center",
      dataFormat: accountType =>
        accountType === "MOBILE" ? (
          <i className="fa fa-mobile fa-lg" />
        ) : (
          <i className="fa fa-laptop fa-lg" />
        ),
      style: { width: "5%", minWidth: "45px" },
    },
    {
      title: "Name",
      dataSort: true,
      dataField: "fullName",
      dataFormat: (_, row) => row.organizationName || row.fullName,
      style: { width: "10%", minWidth: "120px" },
    },
    {
      title: "Account ID",
      dataSort: true,
      dataField: "accountId",
      style: { width: "10%", minWidth: "90px" },
    },
    {
      title: "KHR",
      width: 110,
      dataSort: true,
      dataField: "balanceKhr",
      dataFormat: rielViewFormatter,
    },
    {
      title: "USD",
      width: 110,
      dataSort: true,
      dataField: "balanceUsd",
      dataFormat: dollarViewFormatter,
    },
  ];

  render() {
    const {
      isDirectAccountDataLoading,
      directAccountData,
      directAccountTotalEntities,
      isIndirectAccountDataLoading,
      indirectAccountData,
      indirectAccountTotalEntities,
    } = this.state;

    return (
      <div className="animated fadeIn">
        <Card>
          <CardBody className={styles.totalBalance}>
            <h4 className={styles.header}>Total balance under domain: </h4>
            <CurrencyRoundCard
              currency="riel"
              balance={this.state.totalDomainBalanceKhr}
              data-cy="balance-under-domain-riel"
            />
            <CurrencyRoundCard
              currency="dollar"
              balance={this.state.totalDomainBalanceUsd}
              data-cy="balance-under-domain-dollar"
            />
          </CardBody>
        </Card>
        <Row>
          <Col>
            <Card data-cy="direct">
              <LoadingOverlay loading={this.state.isLoading} />
              <CardHeader
                className={styles.cardHeaderWithIcon}
                data-cy="accounts-branch">
                <img src={bakongIcon} width={27} height={27} alt="" />
                Direct account (number of accounts:{" "}
                {this.state.branchAccountsCount})
              </CardHeader>
              <CardBody style={{ padding: 10 }}>
                <Row>
                  <Col>
                    <CurrenciesCard
                      title="Total direct account"
                      khrBalance={this.state.directBalanceKhr}
                      usdBalance={this.state.directBalanceUsd}
                      data-cy="balance-direct"
                    />
                  </Col>
                  <Col>
                    <CurrenciesCard
                      title="Main account"
                      khrBalance={this.state.mainBalanceKhr}
                      usdBalance={this.state.mainBalanceUsd}
                      data-cy="balance-main"
                    />
                  </Col>
                  <Col>
                    <CurrenciesCard
                      title="Branch account"
                      khrBalance={this.state.branchBalanceKhr}
                      usdBalance={this.state.branchBalanceUsd}
                      data-cy="balance-branch"
                    />
                  </Col>
                </Row>
                <DnDTable
                  onLoadData={this.onLoadDirectAccountData}
                  isLoading={isDirectAccountDataLoading}
                  data={directAccountData}
                  totalEntities={directAccountTotalEntities}
                  requestReport={reports.requestListUserUnderDomain}
                  onDrop={this.onIndirectToDirectDrop}
                  columns={this.branchColumns}
                  sourceType="DIRECT_ACCOUNT"
                  acceptsType="INDIRECT_ACCOUNT"
                  forwardRef={this.directRef}
                />
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card data-cy="indirect">
              <LoadingOverlay loading={this.state.isLoading} />
              <CardHeader
                className={styles.cardHeaderWithIcon}
                data-cy="accounts-indirect">
                <img
                  src={indirectAccountIcon}
                  width={22}
                  height={22}
                  alt=""
                  className={styles.indirectAccountIcon}
                />
                Indirect account (number of accounts:{" "}
                {this.state.indirectAccountsCount})
              </CardHeader>
              <CardBody style={{ padding: 10 }}>
                <Row>
                  <Col>
                    <CurrenciesCard
                      title="Total for indirect account"
                      khrBalance={this.state.indirectBalanceKhr}
                      usdBalance={this.state.indirectBalanceUsd}
                      data-cy="balance-indirect"
                    />
                  </Col>
                </Row>
                <DnDTable
                  onLoadData={this.onLoadIndirectAccountData}
                  isLoading={isIndirectAccountDataLoading}
                  data={indirectAccountData}
                  totalEntities={indirectAccountTotalEntities}
                  requestReport={reports.requestListUserUnderDomain}
                  onDrop={this.onDirectToIndirectDrop}
                  columns={this.customerColumns}
                  sourceType="INDIRECT_ACCOUNT"
                  acceptsType="DIRECT_ACCOUNT"
                  forwardRef={this.indirectRef}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default TotalAccount;
